import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button } from '../components';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { Eye, Flag, FileText, AlertTriangle, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  fetchFiatWachtrij,
  fiatteerMijlpaal,
  wijsMijlpaalAf,
  factureerMijlpalen,
  type TeFiatterenMijlpaal,
} from '../services/projectenApi';

/**
 * Check & Fiat — de grendel van stroom 3.
 *
 * Op de 1e van de maand zet de backend de urenstaten van projectmijlpalen op
 * SUBMITTED. Hier accordeert HR ze één voor één; pas daarna kan er een factuur
 * ontstaan. Het fiat-moment wordt met tijdstempel en IP vastgelegd en komt
 * letterlijk op de factuur te staan, dus dit scherm is bewust expliciet: geen
 * "alles goedkeuren"-knop.
 */

const euro = (waarde: string | number | null | undefined): string => {
  const getal = typeof waarde === 'string' ? parseFloat(waarde) : waarde ?? 0;
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(getal || 0);
};

const EmployerMijlpaalFiatPage: React.FC = () => {
  const { t } = useTranslation('employer');
  useCockpitTitle(t('milestones.title'), t('milestones.subtitle'));
  const queryClient = useQueryClient();

  const [afwijzen, setAfwijzen] = React.useState<{ id: string; reden: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['fiat-wachtrij'],
    queryFn: async () => (await fetchFiatWachtrij()).data?.data as TeFiatterenMijlpaal[],
  });

  const verversen = () => {
    queryClient.invalidateQueries({ queryKey: ['fiat-wachtrij'] });
    queryClient.invalidateQueries({ queryKey: ['employer-invoices'] });
  };

  const fiatMutation = useMutation({
    mutationFn: (milestoneId: string) => fiatteerMijlpaal(milestoneId),
    onSuccess: () => {
      toast.success(t('milestones.approveSuccess'));
      verversen();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('milestones.errorGeneric')),
  });

  const afwijsMutation = useMutation({
    mutationFn: ({ id, reden }: { id: string; reden: string }) => wijsMijlpaalAf(id, reden),
    onSuccess: () => {
      toast.success(t('milestones.rejectSuccess'));
      setAfwijzen(null);
      verversen();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('milestones.errorGeneric')),
  });

  const factuurMutation = useMutation({
    mutationFn: () => factureerMijlpalen(),
    onSuccess: (res) => {
      const aantal = res.data?.data?.length ?? 0;
      toast.success(t('milestones.invoiceSuccess', { count: aantal }));
      verversen();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('milestones.errorGeneric')),
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-center text-gray-600 py-12">{t('milestones.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-center text-red-600 py-12">{t('milestones.loadError')}</p>
      </div>
    );
  }

  const mijlpalen = data || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {mijlpalen.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Flag className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('milestones.empty')}</h2>
            <p className="text-gray-600">{t('milestones.emptyDesc')}</p>
          </div>
        </Card>
      ) : (
        <>
          <div className="mb-4 flex items-start gap-2 rounded-md bg-blue-50 border border-blue-200 p-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900">{t('milestones.fiatNotice')}</p>
          </div>

          <div className="space-y-4">
            {mijlpalen.map((m) => {
              const geschreven = parseFloat(m.geschreven_uren);
              const tarief = m.uurtarief ? parseFloat(m.uurtarief) : null;
              const teFactureren = tarief ? Math.min(geschreven * tarief, parseFloat(m.budget_bedrag)) : parseFloat(m.budget_bedrag);

              return (
                <Card key={m.id} className="hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex flex-wrap justify-between items-start gap-4">
                      <div className="flex-1 min-w-[16rem]">
                        <div className="flex items-center flex-wrap gap-2 mb-2">
                          <Flag className="w-5 h-5 text-purple-600" />
                          <span className="font-mono text-xs text-gray-600">{m.project_code}</span>
                          <h3 className="font-semibold text-gray-900">
                            {t('milestones.milestoneLabel', { nummer: m.volgnummer })}: {m.titel}
                          </h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          {m.project_titel}
                          {m.kvk_handelsnaam ? ` — ${m.kvk_handelsnaam}` : ''}
                        </p>

                        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          <div>
                            <dt className="text-gray-500">{t('milestones.budget')}</dt>
                            <dd className="font-medium text-gray-900">{euro(m.budget_bedrag)}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">{t('milestones.hoursWritten')}</dt>
                            <dd className="font-medium text-gray-900">{geschreven.toFixed(2)}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">{t('milestones.rate')}</dt>
                            <dd className="font-medium text-gray-900">{tarief ? euro(tarief) : '—'}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">{t('milestones.toInvoice')}</dt>
                            <dd className="font-semibold text-gray-900">{euro(teFactureren)}</dd>
                          </div>
                        </dl>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAfwijzen({ id: m.id, reden: '' })}
                        >
                          <X className="w-4 h-4 mr-1" />
                          {t('milestones.reject')}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => fiatMutation.mutate(m.id)}
                          loading={fiatMutation.isPending && fiatMutation.variables === m.id}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {t('milestones.approve')}
                        </Button>
                      </div>
                    </div>

                    {afwijzen?.id === m.id && (
                      <div className="mt-4 border-t border-gray-200 pt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`reden-${m.id}`}>
                          {t('milestones.rejectReason')}
                        </label>
                        <textarea
                          id={`reden-${m.id}`}
                          className="w-full rounded-md border border-gray-300 p-2 text-sm"
                          rows={2}
                          value={afwijzen.reden}
                          onChange={(e) => setAfwijzen({ id: m.id, reden: e.target.value })}
                        />
                        <div className="mt-2 flex gap-2">
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={afwijzen.reden.trim().length < 3}
                            loading={afwijsMutation.isPending}
                            onClick={() => afwijsMutation.mutate({ id: m.id, reden: afwijzen.reden.trim() })}
                          >
                            {t('milestones.confirmReject')}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setAfwijzen(null)}>
                            {t('milestones.cancel')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      <Card className="mt-6">
        <div className="p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">{t('milestones.invoiceApproved')}</h3>
            <p className="text-sm text-gray-600">{t('milestones.invoiceApprovedDesc')}</p>
          </div>
          <Button onClick={() => factuurMutation.mutate()} loading={factuurMutation.isPending}>
            <FileText className="w-4 h-4 mr-1" />
            {t('milestones.generateInvoices')}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default EmployerMijlpaalFiatPage;
